/**
 * OverridesIndicator — Iter63c-A
 *
 * Indicateur dans le header chantier : "X membre(s) avec override actif"
 * Réservé archi/moe_principal (le backend renvoie 403 sinon, on cache si forbidden).
 *
 * Endpoint : GET /api/chantiers/{cid}/overrides-summary
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Shield } from "@phosphor-icons/react";
import { FLAG_LABELS } from "./MemberPermissionsPanel";
import { libelleRoleProjet } from "../../lib/libelles";

const libelleDroit = (f) => FLAG_LABELS[f] || f;

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function OverridesIndicator({ chantierId, canSee = true }) {
  const [data, setData] = useState({ count: 0, members: [] });
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    if (!chantierId || !canSee) return;
    setLoading(true);
    axios
      .get(`${API_URL}/api/chantiers/${chantierId}/overrides-summary`, { withCredentials: true })
      .then((r) => {
        setData(r.data || { count: 0, members: [] });
        setForbidden(false);
      })
      .catch((e) => {
        if (e?.response?.status === 403) setForbidden(true);
      })
      .finally(() => setLoading(false));
  }, [chantierId, canSee]);

  if (!canSee || forbidden || loading || data.count === 0) return null;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-1 text-xs px-2 py-1 bg-amber-100 hover:bg-amber-200 text-amber-800 rounded-sm transition-colors"
          data-testid="overrides-indicator"
          title="Voir les membres à qui un droit a été confié ou retiré"
        >
          <Shield size={14} weight="fill" />
          {data.count} membre{data.count > 1 ? "s" : ""} avec droits ajustés
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 max-h-96 overflow-y-auto p-3" align="end">
        <div className="text-sm font-medium text-[#2C2C2C] mb-2" style={{ fontFamily: 'DM Serif Display, serif' }}>
          Droits confiés ou retirés
        </div>
        <div className="space-y-2">
          {data.members.map((m) => (
            <div key={m.user_id} className="border-l-2 border-amber-300 pl-2 py-1" data-testid={`override-member-${m.user_id}`}>
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-[#2C2C2C]">{m.name}</div>
                <div className="text-[10px] uppercase tracking-wide text-[#6B6B6B]">{libelleRoleProjet(m.role_projet)}</div>
              </div>
              {m.granted?.length ? (
                <div className="text-xs text-emerald-700 mt-0.5">+ {m.granted.map(libelleDroit).join(", ")}</div>
              ) : null}
              {m.revoked?.length ? (
                <div className="text-xs text-rose-700 mt-0.5">− {m.revoked.map(libelleDroit).join(", ")}</div>
              ) : null}
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
